import { getPublicKeys } from '../utils/storage';

/**
 * Apple Pay through Collect.js.
 *
 * The shape of this integration is unlike Stripe's or Recurly's. There the app
 * renders its own button and drives a `paymentRequest` object; Collect.js
 * instead renders the Apple Pay button *itself* into a container we point it at,
 * and hands the wallet token back through the same tokenization callback as the
 * card fields. So there is no button component here — only the field config.
 *
 * Collect.js validates this config aggressively and throws on anything it does
 * not recognise, so every key below is one it explicitly accepts.
 *
 * The recurring block is what makes the wallet issue a REUSABLE token. Without it
 * Apple hands over a one-shot payment, the card never lands in the vault, and
 * every later charge — upsells and rebills alike — is declined. Apple requires the
 * terms to be shown in the sheet in exchange: there is no way to get a
 * merchant-initiated token without telling the payer it will be reused.
 */

/**
 * Apple's `ApplePayRecurringPaymentDateUnit`. Deliberately narrower than what
 * Collect.js validates against (it also accepts 'week'): WebKit has no 'week' and
 * throws a bare "Type error" from the ApplePaySession constructor when given one.
 */
type PeriodUnit = 'day' | 'month' | 'year';

export type TailoredpayApplePayField = Record<string, any>;

type BuildParams = {
  /** CSS selector of the container Collect.js mounts the button into. */
  selector: string;
  product: any;
  /** Shown as the payee in the native sheet. */
  label: string;
  buttonStyle?: 'black' | 'white' | 'white-outline';
  height?: string;
  borderRadius?: string;
  /** Where the user can cancel — Apple requires it for recurring payments. */
  managementUrl?: string;
  /**
   * `collectApplePayBillingData`. Adds the billing postal address to what the
   * native sheet hands back — NMI uses the postal code for AVS. Also what makes
   * the sheet show its "Billing Details" row: off => no billing contact fields are
   * requested at all and the row is absent.
   */
  collectBillingData?: boolean;
  /** `requestWalletEmail` (or the pre-rename `requestApplePayEmail` on funnels
   * that have not migrated). Asks the sheet for the payer's email. */
  requestEmail?: boolean;
  /**
   * Drops the recurring block. Set after the SDK reports that the device (or the
   * gateway account) predates Apple Pay v14 and cannot display subscription
   * terms — keeping the block would make Collect.js throw instead of rendering
   * a button. The token is then one-shot again, but a button that works beats no
   * button at all.
   */
  withoutRecurring?: boolean;
};

// Weeks are expressed in days, since Apple has no week unit — 'P1W' is 7 days,
// which the sheet renders as "every 7 days".
const PERIOD_UNITS: Record<string, { unit: PeriodUnit; perUnit: number }> = {
  D: { unit: 'day', perUnit: 1 },
  W: { unit: 'day', perUnit: 7 },
  M: { unit: 'month', perUnit: 1 },
  Y: { unit: 'year', perUnit: 1 },
};

/** 'P1W' -> { count: 7, unit: 'day' }. Null for ONETIME or anything unparsable. */
const parsePeriod = (period?: string) => {
  const match = /^P(\d+)([DWMY])$/.exec(String(period || '').toUpperCase());

  if (!match) {
    return null;
  }

  const count = Number(match[1]);
  const mapped = PERIOD_UNITS[match[2]];

  return count > 0 && mapped
    ? { count: count * mapped.perUnit, unit: mapped.unit }
    : null;
};

/** Apple wants amounts as decimal strings, not numbers. */
const toAmount = (value: any) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : null;
};

const addDays = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
};

/**
 * Whether the gateway account has Apple Pay turned on — `apple_pay_enabled` from
 * GET /nmi/config, stashed with the rest of the public keys during billing init.
 * Device support is a separate question, and Collect.js answers that one itself
 * by simply not mounting a button.
 */
export const isApplePayEnabled = () =>
  Boolean((getPublicKeys() as any)?.tailoredpay?.apple_pay_enabled);

/**
 * The billing address as the gateway wants it: NMI's transaction fields, so the
 * backend can forward it verbatim. Used for the card form's ZIP — the Apple Pay
 * address needs no help, since Collect.js attaches it to the token itself.
 *
 * Returns null when there is nothing to send, so the request body stays exactly
 * as it was before this was collected.
 */
export const toBillingAddress = (source: {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}) => {
  const address = {
    address1: source.address1 || undefined,
    address2: source.address2 || undefined,
    city: source.city || undefined,
    state: source.state || undefined,
    zip: source.postalCode || undefined,
    country: source.country || undefined,
  };

  return Object.values(address).some(Boolean) ? address : null;
};

export const buildApplePayField = ({
  selector,
  product,
  label,
  buttonStyle = 'black',
  height = '50px',
  borderRadius = '8px',
  managementUrl,
  collectBillingData,
  requestEmail,
  withoutRecurring,
}: BuildParams): TailoredpayApplePayField => {
  const recurringAmount = toAmount(product?.amount);
  const period = parsePeriod(product?.period);
  const isRecurring = Boolean(
    !withoutRecurring && !product?.isOneTimePurchase && recurringAmount && period,
  );

  const field: TailoredpayApplePayField = {
    selector,
    // The mark alone —  Pay, with no leading verb. Every other type prepends one
    // ('Buy with', 'Subscribe with', even 'pay' gives 'Pay with'), and the default
    // is 'buy', so this has to be set explicitly.
    type: 'plain',
    style: {
      'button-style': buttonStyle,
      height,
      'border-radius': borderRadius,
    },
    totalLabel: label,
    // Requesting ANY billing contact field is what makes the sheet render its
    // "Billing Details" row, so when we are not collecting billing data the key is
    // omitted entirely rather than narrowed to ['name'] — an empty array still
    // counts as a request. The cost is that the wallet no longer hands back the
    // payer's name, so `handleWalletToken` has only the card form's own inputs to
    // attribute the purchase to.
    ...(collectBillingData
      ? { requiredBillingContactFields: ['name', 'postalAddress'] }
      : {}),
    // Mirrors the Stripe rule: the email is never forced by the billing flag, only
    // requested when `requestApplePayEmail` says so.
    ...(requestEmail ? { contactFields: ['email'] } : {}),
  };

  // The extra checks are what `isRecurring` is made of — repeated so the compiler
  // knows they hold below, since this project builds with `strict: false`.
  if (!isRecurring || !recurringAmount || !period) {
    return field;
  }

  const trialDays = Number(product?.trialDays ?? product?.trial_period_days ?? 0) || 0;
  // What is charged today. NOTE: with a recurring request attached, Apple takes the
  // sheet's total from `recurringUpFrontPrice` and NOT from `price` — and defaults
  // it to "0.00" when it is absent. So it is always sent, or the user would be
  // asked to authorize nothing and then be charged.
  const upFrontPrice =
    toAmount(product?.trialPriceAmount ?? product?.trial_price_amount ?? product?.amount)
    || '0.00';

  // Collect.js validates the recurring block as a unit: with
  // `isRecurringTransaction` on, every one of these is mandatory and a missing
  // one throws. They are only ever set together.
  return {
    ...field,
    isRecurringTransaction: true,
    recurringPaymentDescription: label,
    recurringLabel: label,
    recurringAmount,
    recurringPaymentTiming: 'recurring',
    // When the plan actually starts charging — after the trial, if there is one.
    recurringPaymentStartDate: addDays(trialDays).toISOString(),
    recurringPaymentIntervalUnit: period.unit,
    recurringPaymentIntervalCount: period.count,
    recurringManagementUrl: managementUrl || `${window.location.origin}/manage`,
    recurringUpFrontPrice: upFrontPrice,
  };
};
